import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  ShieldCheck,
  Compass,
  GraduationCap,
  Users,
  UserCog,
  FileSearch,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { Header } from "../components/Header";
import { Footer } from "../components/Footer";
import { PrincipleChain } from "../components/PrincipleChain";
import { Button } from "../components/ui/button";
import api from "../lib/api";

const HERO_IMG =
  "https://images.unsplash.com/photo-1690805109760-2022f1971e36?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwyfHxub3JkaWMlMjBsaWJyYXJ5JTIwYXJjaGl0ZWN0dXJlJTIwc3VubGlnaHQlMjBmb2N1c3xlbnwwfHx8fDE3ODg4OTEwOTZ8MA&ixlib=rb-4.1.0&q=85";

function Section({ id, children, className = "" }) {
  return (
    <section id={id} className={`max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 ${className}`}>
      {children}
    </section>
  );
}

function Reveal({ children, delay = 0 }) {
  const reduce = useReducedMotion();
  return (
    <motion.div
      initial={reduce ? false : { opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5, delay }}
    >
      {children}
    </motion.div>
  );
}

export default function Landing() {
  const { t, lang } = useLanguage();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [framework, setFramework] = useState(null);

  useEffect(() => {
    api
      .get("/meta/service-boundaries")
      .catch(() => {});
  }, []);

  useEffect(() => {
    // Public provenance sample requires auth; show static shell if unauthenticated.
    api
      .get("/curriculum/frameworks")
      .then((r) => setFramework(r.data.frameworks?.[0]))
      .catch(() => setFramework(null));
  }, []);

  const roles = [
    { icon: GraduationCap, ...t("roles.student"), key: "student" },
    { icon: Users, ...t("roles.teacher"), key: "teacher" },
    { icon: ShieldCheck, ...t("roles.guardian"), key: "guardian" },
    { icon: UserCog, ...t("roles.admin"), key: "admin" },
  ];

  return (
    <div className="App bg-[#FBFBF9] overflow-x-hidden" data-testid="landing-page">
      <Header />

      <main>

      {/* Hero */}
      <Section className="pt-16 sm:pt-24 pb-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-6">
            <motion.div
              initial={reduce ? false : { opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <span className="font-mono-label text-[10px] text-[#5B655F] inline-flex items-center gap-2 rounded-full border border-[#1A1F1C]/10 bg-white px-3 py-1.5">
                <span className="h-1.5 w-1.5 rounded-full bg-[#C27B2B]" />
                {t("hero.badge")}
              </span>
              <h1 className="mt-6 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.08] text-[#1A1F1C]">
                {t("hero.title")}
              </h1>
              <p className="mt-6 text-lg text-[#525B56] leading-relaxed max-w-xl">
                {t("hero.subtitle")}
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Button
                  size="lg"
                  onClick={() => navigate("/register")}
                  data-testid="hero-primary-cta"
                  className="rounded-full bg-[#1E3A2B] hover:bg-[#14281E] text-[#FBFBF9] px-6 group"
                >
                  {t("hero.primaryCta")}
                  <ArrowRight className="ml-1.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
                <Button
                  size="lg"
                  variant="outline"
                  onClick={() => document.getElementById("principle")?.scrollIntoView()}
                  data-testid="hero-secondary-cta"
                  className="rounded-full border-[#1A1F1C]/15 bg-transparent text-[#1A1F1C] hover:bg-white px-6"
                >
                  {t("hero.secondaryCta")}
                </Button>
              </div>
              <div className="mt-10 flex items-start gap-3 rounded-2xl border border-[#C27B2B]/25 bg-[#FEF3C7]/40 p-4 max-w-xl">
                <Compass className="h-5 w-5 text-[#C27B2B] shrink-0 mt-0.5" />
                <p className="text-sm text-[#525B56] leading-relaxed">{t("hero.identity")}</p>
              </div>
            </motion.div>
          </div>

          <div className="lg:col-span-6">
            <motion.div
              initial={reduce ? false : { opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="relative rounded-[1.75rem] overflow-hidden border border-[#1A1F1C]/10 shadow-[0_20px_60px_rgba(26,31,28,0.12)]"
            >
              <img
                src={HERO_IMG}
                alt={lang === "sv" ? "Ljus, lugn studiemiljö" : "Bright, calm study environment"}
                className="w-full h-[420px] lg:h-[520px] object-cover"
              />
              <div className="absolute inset-x-0 bottom-0 p-5 bg-gradient-to-t from-[#1A1F1C]/70 to-transparent">
                <p className="font-mono-label text-[10px] text-[#FBFBF9]/90">
                  Evidence → Understanding → Next step → Practice → Reassessment → Progress
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </Section>

      {/* How Reach New Edge thinks */}
      <div id="thinks" className="bg-[#0F1F17]">
        <Section className="py-16 sm:py-24 lg:py-28">
          <Reveal>
            <div className="max-w-2xl">
              <span className="font-mono-label text-[10px] text-[#E3A24E]">{t("thinks.label")}</span>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#FBFBF9]">
                {t("thinks.title")}
              </h2>
              <p className="mt-4 text-lg text-[#D8E3DC] leading-relaxed">{t("thinks.intro")}</p>
            </div>
            <div className="mt-12" aria-hidden="true">
              <svg viewBox="0 0 1200 120" className="w-full h-[70px]" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="edgePath" x1="0" x2="1">
                    <stop offset="0" stopColor="#2B4A38" stopOpacity="0.5" />
                    <stop offset="1" stopColor="#E3A24E" />
                  </linearGradient>
                </defs>
                <path d="M0 100 C300 100 520 36 1180 22" fill="none" stroke="url(#edgePath)" strokeWidth="3" strokeLinecap="round" />
                <circle cx="1180" cy="22" r="7" fill="#E3A24E" />
              </svg>
            </div>
            <ol className="mt-8 grid grid-cols-2 lg:grid-cols-4 gap-4">
              {t("thinks.stages").map((s, i) => (
                <li key={i} data-testid={`thinks-stage-${i}`} className="rounded-2xl border border-[#FBFBF9]/12 bg-[#14281E] p-5">
                  <span className="font-mono-label text-[10px] text-[#E3A24E]">0{i + 1}</span>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-[#FBFBF9]">{s.k}</h3>
                  <p className="mt-1 text-sm text-[#D8E3DC] leading-relaxed">{s.d}</p>
                </li>
              ))}
            </ol>
            <p className="mt-8 font-mono-label text-[10px] text-[#FBFBF9]/70">{t("thinks.flow")}</p>
          </Reveal>
        </Section>
      </div>

      {/* Mission */}
      <Section id="mission" className="py-16 sm:py-24 lg:py-28">
        <Reveal>
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <span className="font-mono-label text-[10px] text-[#5B655F]">{t("mission.label")}</span>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1A1F1C]">
                {t("mission.title")}
              </h2>
            </div>
            <div className="lg:col-span-7">
              <p className="text-lg text-[#525B56] leading-relaxed">{t("mission.body")}</p>
              <ul className="mt-8 space-y-4">
                {t("mission.points").map((p, i) => (
                  <li key={i} className="flex items-start gap-3">
                    <span className="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#D8E3DC] font-mono-label text-[10px] text-[#1E3A2B]">
                      {i + 1}
                    </span>
                    <span className="text-base text-[#1A1F1C]">{p}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </Reveal>
      </Section>

      {/* Principle chain */}
      <div id="principle" className="bg-[#F4F4F0] border-y border-[#1A1F1C]/8">
        <Section className="py-16 sm:py-24 lg:py-28">
          <Reveal>
            <div className="max-w-2xl mb-10">
              <span className="font-mono-label text-[10px] text-[#5B655F]">{t("principle.label")}</span>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1A1F1C]">
                {t("principle.title")}
              </h2>
              <p className="mt-4 text-lg text-[#525B56] leading-relaxed">{t("principle.intro")}</p>
            </div>
            <PrincipleChain />
          </Reveal>
        </Section>
      </div>

      {/* Methodology / Roles */}
      <Section id="method" className="py-16 sm:py-24 lg:py-28">
        <Reveal>
          <div id="roles" className="max-w-2xl mb-12">
            <span className="font-mono-label text-[10px] text-[#5B655F]">{t("roles.label")}</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1A1F1C]">
              {t("roles.title")}
            </h2>
            <p className="mt-4 text-base text-[#525B56] leading-relaxed">{t("roles.subtitle")}</p>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {roles.map((r, i) => {
            const Icon = r.icon;
            return (
              <Reveal key={r.key} delay={i * 0.06}>
                <div
                  data-testid={`role-card-${r.key}`}
                  className="h-full rounded-2xl border border-[#1A1F1C]/10 bg-white p-6 sm:p-8 shadow-[0_2px_8px_rgba(0,0,0,0.03)] hover:shadow-[0_10px_30px_rgba(0,0,0,0.06)] transition-shadow duration-300"
                >
                  <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-[#D8E3DC]">
                    <Icon className="h-5 w-5 text-[#1E3A2B]" />
                  </span>
                  <h3 className="mt-5 font-heading text-xl font-semibold text-[#1A1F1C]">{r.name}</h3>
                  <p className="mt-2 text-base text-[#525B56] leading-relaxed">{r.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </Section>

      {/* For students */}
      <Section id="students" className="py-16 sm:py-24">
        <Reveal>
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            <div>
              <span className="font-mono-label text-[10px] text-[#5B655F]">{t("students.label")}</span>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1A1F1C]">{t("students.title")}</h2>
            </div>
            <p className="text-lg text-[#525B56] leading-relaxed">{t("students.body")}</p>
          </div>
        </Reveal>
      </Section>

      {/* For teachers */}
      <div className="bg-[#F4F4F0] border-y border-[#1A1F1C]/8">
        <Section id="teachers" className="py-16 sm:py-24">
          <Reveal>
            <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
              <div>
                <span className="font-mono-label text-[10px] text-[#5B655F]">{t("teachers.label")}</span>
                <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1A1F1C]">{t("teachers.title")}</h2>
              </div>
              <p className="text-lg text-[#525B56] leading-relaxed">{t("teachers.body")}</p>
            </div>
          </Reveal>
        </Section>
      </div>

      {/* Trust principles */}
      <Section id="trust" className="py-16 sm:py-24">
        <Reveal>
          <div className="max-w-2xl mb-10">
            <span className="font-mono-label text-[10px] text-[#5B655F]">{t("trust.label")}</span>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#1A1F1C]">{t("trust.title")}</h2>
          </div>
          <ul className="grid sm:grid-cols-2 gap-4">
            {t("trust.items").map((p, i) => (
              <li key={i} data-testid={`trust-principle-${i}`} className="flex items-start gap-3 rounded-2xl border border-[#1A1F1C]/10 bg-white p-5">
                <span className="mt-0.5 font-mono-label text-[10px] text-[#1E3A2B]">0{i + 1}</span>
                <span className="text-base text-[#1A1F1C]">{p}</span>
              </li>
            ))}
          </ul>
        </Reveal>
      </Section>

      {/* Provenance */}
      <div className="bg-[#1E3A2B]">
        <Section className="py-16 sm:py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <span className="font-mono-label text-[10px] text-[#E3A24E]">{t("provenance.label")}</span>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#FBFBF9]">
                {t("provenance.title")}
              </h2>
              <p className="mt-4 text-base text-[#D8E3DC] leading-relaxed max-w-lg">{t("provenance.body")}</p>
              <p className="mt-4 font-mono-label text-[10px] text-[#FBFBF9]/60">{t("provenance.note")}</p>
            </div>
            <div className="lg:col-span-6">
              <div className="rounded-2xl border border-[#FBFBF9]/15 bg-[#14281E] p-6 sm:p-8" data-testid="provenance-card">
                <div className="flex items-center gap-3 mb-6">
                  <FileSearch className="h-5 w-5 text-[#C27B2B]" />
                  <span className="font-heading font-semibold text-[#FBFBF9]">
                    {framework ? framework.title : "Gy25 — Gymnasieskolan"}
                  </span>
                </div>
                <dl className="space-y-4 text-sm">
                  <div className="flex justify-between gap-4 border-b border-[#FBFBF9]/10 pb-3">
                    <dt className="text-[#D8E3DC]/70">{t("provenance.framework")}</dt>
                    <dd className="text-[#FBFBF9] font-mono-label text-[10px]">Gy25</dd>
                  </div>
                  <div className="flex justify-between gap-4 border-b border-[#FBFBF9]/10 pb-3">
                    <dt className="text-[#D8E3DC]/70">{t("provenance.authority")}</dt>
                    <dd className="text-[#FBFBF9]">Skolverket</dd>
                  </div>
                  <div className="flex justify-between gap-4">
                    <dt className="text-[#D8E3DC]/70">{t("provenance.status")}</dt>
                    <dd>
                      <span className="rounded-full bg-[#C27B2B]/20 px-2.5 py-1 font-mono-label text-[9px] text-[#F0BE72]">
                        {t("provenance.unverified")}
                      </span>
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>
        </Section>
      </div>

      </main>

      <Footer />
    </div>
  );
}
